import {
  FileUploadResponse,
  FileUploadResult,
  QuotaStatus,
  UploadMetadata,
  UploadPresignResult,
  ALLOWED_AUDIO_TYPES,
  ALLOWED_EXTENSIONS,
  MAX_FILE_SIZE,
  MAX_FILES_PER_UPLOAD,
} from '../types/Upload';
import { authService } from './authService';
import { apiClient } from './apiClient';
import { Config } from '../config';

/** Thrown when the direct-to-blob PUT fails (firewall, CORS, expired SAS). The
 *  UploadContext disables the direct path for the rest of the session and falls
 *  back to the relay endpoint, so environments without the storage changes keep working. */
export class DirectUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DirectUploadError';
  }
}

const timezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export class UploadService {
  /**
   * Per-file upload primitives. Orchestration (worker pool, retries, relay fallback)
   * lives in UploadContext so uploads survive route changes.
   */

  static validateFile(file: File): string[] {
    const errors: string[] = [];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
    }

    // Check file extension
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      errors.push(
        `File extension '${extension}' is not allowed. Allowed extensions: ${ALLOWED_EXTENSIONS.join(', ')}`
      );
    }

    // Check content type
    if (!ALLOWED_AUDIO_TYPES.includes(file.type.toLowerCase())) {
      errors.push(`Content type '${file.type}' is not allowed`);
    }

    return errors;
  }

  static validateUploadRequest(files: File[]): string[] {
    const errors: string[] = [];

    if (!files || files.length === 0) {
      errors.push('No files provided');
      return errors;
    }

    if (files.length > MAX_FILES_PER_UPLOAD) {
      errors.push(`Too many files. Maximum allowed: ${MAX_FILES_PER_UPLOAD}`);
    }

    files.forEach((file) => {
      const fileErrors = this.validateFile(file);
      fileErrors.forEach((error) => {
        errors.push(`${file.name}: ${error}`);
      });
    });

    return errors;
  }

  static async getQuotaStatus(): Promise<QuotaStatus> {
    const isAuthenticated = await authService.ensureAuthenticated();
    if (!isAuthenticated) {
      throw new Error('Authentication failed');
    }

    const token = await authService.getToken();
    const response = await fetch(`${Config.api.baseUrl}/api/calls/quota`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Timezone': timezone(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch quota status: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Reserves quota and obtains a short-lived write-only SAS for one file.
   * Returns a failed result (never throws) for quota/validation rejections so the
   * caller can distinguish "stop" (quota) from "fall back to relay" (anything else).
   */
  static async presignFile(file: File): Promise<UploadPresignResult> {
    try {
      const response = await apiClient.post<UploadPresignResult>(
        '/api/calls/upload/presign',
        {
          fileName: file.name,
          fileSize: file.size,
        },
        { headers: { 'X-Timezone': timezone() } }
      );
      return response.data;
    } catch (error) {
      const data = (error as { response?: { data?: UploadPresignResult } })?.response?.data;
      if (data && typeof data === 'object' && 'quotaExceeded' in data) {
        return data;
      }
      throw error;
    }
  }

  /**
   * PUTs the file straight to Azure Blob Storage using the presigned SAS URL.
   * No Authorization header — the SAS query string carries the (write-only) grant.
   */
  static uploadToBlob(file: File, sasUrl: string, onProgress?: (progress: number) => void): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 201) {
          resolve();
        } else {
          reject(new DirectUploadError(`Blob upload failed with status ${xhr.status}`));
        }
      });
      xhr.addEventListener('error', () =>
        reject(new DirectUploadError('Blob upload blocked or unreachable')));
      xhr.addEventListener('timeout', () =>
        reject(new DirectUploadError('Blob upload timed out')));

      xhr.open('PUT', sasUrl);
      xhr.timeout = 1800000; // 30 minutes — generous ceiling for a single large file
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
  }

  static async confirmFile(callId: string): Promise<FileUploadResult> {
    const response = await apiClient.post<FileUploadResult>(`/api/calls/upload/confirm/${callId}`);
    return response.data;
  }

  /**
   * Reconciliation probe: asks whether a call already exists for this upload's idempotency key.
   * Called after a relay network error/timeout to distinguish a real failure from a lost-response
   * success (the server committed the upload, but the 504/dropped connection hid the result).
   * Best-effort — returns false if the check itself fails, so a genuine failure is never masked.
   */
  static async checkUploadStatus(dedupeId: string): Promise<boolean> {
    try {
      const response = await apiClient.get<{ exists: boolean; callId?: string }>(
        `/api/calls/upload/status/${dedupeId}`
      );
      return response.data.exists;
    } catch {
      return false;
    }
  }

  /** Best-effort: releases the quota reservation when falling back to the relay path. */
  static async abandonFile(callId: string): Promise<void> {
    try {
      await apiClient.post(`/api/calls/upload/abandon/${callId}`);
    } catch {
      // Non-fatal — the backend orphan sweep reclaims it within ~30 minutes.
    }
  }

  /**
   * Relay fallback: uploads a single file as its own multipart request through the API.
   * Used when the direct-to-blob path is unavailable (storage firewall, missing RBAC).
   */
  static async uploadFileViaRelay(
    file: File,
    metadata: UploadMetadata,
    dedupeId: string,
    onProgress?: (progress: number) => void
  ): Promise<FileUploadResult> {
    // Token re-acquired per file: MSAL silently refreshes near-expiry tokens, so
    // multi-hour upload sessions never hit a stale-token 401 on the tail files.
    const token = await authService.getToken();

    const formData = new FormData();
    // Backend binds IFormFileCollection named "Files" — one entry per request.
    formData.append('Files', file);

    if (metadata.incidentNumber) {
      formData.append('IncidentNumber', metadata.incidentNumber);
    }
    formData.append('CallType', metadata.callType);
    if (metadata.callDate) {
      formData.append('CallDate', metadata.callDate);
    }

    const response = await new Promise<FileUploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        });
      }

      xhr.addEventListener('load', () => {
        // 2xx and 207 (Multi-Status) and 429 (quota) all carry a JSON body we can use.
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(parsed);
          } else if (xhr.status === 207 || xhr.status === 429 || xhr.status === 400) {
            resolve(parsed);
          } else {
            reject(new Error(parsed.message || `Upload failed with status ${xhr.status}`));
          }
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error occurred during upload')));
      xhr.addEventListener('timeout', () => reject(new Error('Upload timeout')));

      xhr.open('POST', `${Config.api.baseUrl}/api/calls/upload`);
      xhr.timeout = 1800000; // 30 minutes — generous ceiling for a single large file

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.setRequestHeader('X-Timezone', timezone());
      // Idempotency key: the same value rides every retry of this file, so a re-POST after a
      // lost response resolves to the existing call server-side instead of creating a duplicate.
      xhr.setRequestHeader('X-Upload-Dedupe-Id', dedupeId);

      xhr.send(formData);
    });

    // Each request carries exactly one file → exactly one result.
    const single = response.results?.[0];
    if (single) {
      return single;
    }

    // Defensive fallback: server returned a body without per-file results.
    return {
      callId: '',
      fileName: file.name,
      blobUrl: '',
      fileSize: file.size,
      contentType: file.type,
      success: response.successfulUploads > 0,
      errorMessage: response.errors?.[0],
    };
  }

  /**
   * Signals that the current upload session is finished. The backend's submission sweep
   * uses this to batch the whole session into a single Azure Speech request.
   */
  static async completeUploadSession(): Promise<void> {
    try {
      // apiClient attaches the bearer token via its request interceptor.
      await apiClient.post('/api/calls/upload/session/complete', null, {
        headers: { 'X-Timezone': timezone() },
      });
    } catch (error) {
      // Non-fatal — the backend's MaxHold guard still submits the session.
      console.warn('Failed to signal upload session completion:', error);
    }
  }
}
